import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import BLOG_POSTS from "@/data/blogData";
import { getReadingLabel } from "@/utils/readingTime";
import useLatestBlogPosts from "@/hooks/useLatestBlogPosts";
import { getBlogImage } from "@/utils/blogImage";

function BlogCard({ post, priority = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`home-blog-card-${post.slug}`}
      className="glass-card glass-card-hover group flex flex-col overflow-hidden rounded-2xl"
    >
      <div className="relative h-48 md:h-52 overflow-hidden">
        <img
          src={getBlogImage(post)}
          alt={post.title}
          width="400"
          height="208"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80"; }}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050A11] via-transparent to-transparent" />
        <span className="absolute top-4 left-4 text-[10px] font-bold tracking-[0.15em] uppercase text-blue-300 bg-blue-600/20 border border-blue-500/30 backdrop-blur-sm px-3 py-1 rounded-full">
          {post.category}
        </span>
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center gap-4 text-slate-500 text-xs mb-3">
          <span className="flex items-center gap-1.5">
            <Calendar size={12} />
            {new Date(post.date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={12} />
            {getReadingLabel(post)}
          </span>
        </div>
        <h3
          className="text-lg font-medium mb-3 leading-snug group-hover:text-blue-300 transition-colors line-clamp-2"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {post.title}
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 mb-5">
          {post.excerpt}
        </p>
        <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-blue-400 group-hover:text-blue-300 transition-colors">
          Read Article
          <ArrowRight
            size={12}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </span>
      </div>
    </Link>
  );
}

export default function LatestBlog() {
  const posts = useLatestBlogPosts(BLOG_POSTS, 3);

  if (!posts.length) return null;

  return (
    <section
      id="latest-blog"
      data-testid="latest-blog-section"
      className="py-24 md:py-32 border-t border-white/5"
      aria-label="Latest from the Blog"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">
              Expert Insights
            </p>
            <h2
              className="text-3xl md:text-4xl tracking-tight font-medium mb-3"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Latest from the Blog
            </h2>
            <p className="text-slate-400 text-sm md:text-base max-w-xl">
              Tips, guides, and insights for luxury smart homes, TV mounting, and IT across Southwest Florida.
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="latest-blog-view-all"
            className="inline-flex items-center min-h-[44px] gap-2 border border-white/15 hover:border-white/30 hover:bg-white/5 text-white font-medium px-5 py-3 rounded-lg transition-all text-sm self-start"
          >
            View All Articles <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {posts.map((p) => (
            <BlogCard key={p.slug} post={p} priority />
          ))}
        </div>
      </div>
    </section>
  );
}
